import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useBookings, useVehicles, useChauffeurs, useInvalidateRental } from "../hooks/useRentalData";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Plus, ArrowRight, CheckCircle, Car, UserCircle as IconChauffeur, ArrowUUpLeft, MapTrifold, UploadSimple, Warning, Globe, PhoneCall } from "@phosphor-icons/react";
import { toast } from "sonner";
import { RENTAL_FIELDS, CHAUFFEUR_FIELDS, TOUR_FIELDS, CLIENT_CREDENTIAL_FIELDS, validateBooking, todayISODate } from "./bookingFields";
import { BookingForm } from "../components/BookingForm";
import TourPackages from "./TourPackages";
import { BookingTable } from "../components/BookingTable";
import { QuoteDialog } from "../components/QuoteDialog";
import DriversPanel from "../components/DriversPanel";
import SalespeoplePanel from "../components/SalespeoplePanel";
import { ChauffeurImportSummary } from "../components/ChauffeurImportSummary";
import OwnerBalancePanel from "../components/OwnerBalancePanel";
import { WebsiteContactDialog } from "../components/booking/WebsiteContactDialog";
import { useLeadsTeam } from "../lib/leadsTeam";
import { LeadMatchDialog, useLeadMatch } from "../components/LeadMatchDialog";
import { PillTabs } from "../components/lux";
import { driverPayFor } from "../lib/chauffeurSplit";
import { useAuth } from "../contexts/AuthContext";

export default function Bookings() {
  const { section } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = section === "rental" ? "rental_pre" : section === "chauffeur" ? "chauffeur_pre" : section === "tours" ? "tours_pre" : section === "submitted" ? "submitted" : "pre_booking";
  const { data: bookings = [], isLoading: loading } = useBookings();
  const { data: vehicles = [] } = useVehicles();
  const { data: chauffeurs = [] } = useChauffeurs();
  const invalidate = useInvalidateRental();
  // Tab is sourced from ?tab= so returning from a detail view (or a reload)
  // lands on the same tab the user was on, not the first one.
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState(urlTab || initial);
  const changeTab = (val) => {
    setTab(val);
    const next = new URLSearchParams(searchParams);
    next.set("tab", val);
    setSearchParams(next, { replace: true });
  };
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const onImportChauffeur = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bookings/import-chauffeur", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      invalidate();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  useEffect(() => { setTab(urlTab || initial); }, [section, urlTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [typePromptOpen, setTypePromptOpen] = useState(false);
  const [subPromptOpen, setSubPromptOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formType, setFormType] = useState("rental");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [quoteBooking, setQuoteBooking] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [contactTarget, setContactTarget] = useState(null);
  // WEB-001: the one-time "Move all to Leads" for website requests, admin only.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [moveRows, setMoveRows] = useState(null);
  const [moving, setMoving] = useState(false);
  const moveAll = async () => {
    setMoving(true);
    try {
      const { data } = await api.post("/website-requests/move-to-leads", { ids: moveRows.map((b) => b.id) });
      const n = (data.moved || []).length;
      toast.success(`${n} website ${n === 1 ? "request" : "requests"} moved to Leads`);
      if ((data.skipped || []).length) toast.message(`${data.skipped.length} skipped: already moved or no longer waiting`);
      setMoveRows(null);
      invalidate();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMoving(false); }
  };
  const leadsTeam = useLeadsTeam();

  const filtered = useMemo(() => {
    const toTime = (s) => {
      if (!s) return 0;
      // Try native parse first (ISO)
      const t = Date.parse(s);
      if (!isNaN(t)) return t;
      // Handle messy CSV formats like "September /16/2025 9:0" or "October /4/2024 10:0"
      const cleaned = String(s).replace(/\s*\/\s*/g, " ").replace(/\s+/g, " ").trim();
      const t2 = Date.parse(cleaned);
      if (!isNaN(t2)) return t2;
      return 0;
    };
    const byDateDesc = (a, b) => toTime(b.start_datetime) - toTime(a.start_datetime);
    // Pre Bookings: soonest (nearest date) at top, farthest-out at the bottom.
    const byDateAsc = (a, b) => toTime(a.start_datetime) - toTime(b.start_datetime);
    return {
      submitted: bookings.filter((b) => b.status === "submitted").sort(byDateAsc),
      submitted_website: bookings.filter((b) => b.status === "submitted" && b.source === "website").sort(byDateAsc),
      submitted_rental: bookings.filter((b) => b.status === "submitted" && b.source !== "website" && b.booking_type === "rental").sort(byDateAsc),
      submitted_chauffeur_tour: bookings.filter((b) => b.status === "submitted" && b.source !== "website" && (b.booking_type === "chauffeur" || b.booking_type === "tour")).sort(byDateAsc),
      pre_booking: bookings.filter((b) => b.status === "pre_booking").sort(byDateAsc),
      rental_pre: bookings.filter((b) => b.booking_type === "rental" && b.status === "pre_booking").sort(byDateAsc),
      rental_current: bookings.filter((b) => b.booking_type === "rental" && b.status === "current").sort(byDateDesc),
      rental_completed: bookings.filter((b) => b.booking_type === "rental" && b.status === "completed").sort(byDateDesc),
      chauffeur_pre: bookings.filter((b) => b.booking_type === "chauffeur" && (b.status === "pre_booking" || b.status === "current")).sort(byDateAsc),
      chauffeur_completed: bookings.filter((b) => b.booking_type === "chauffeur" && b.status === "completed" && !b.driver_paid).sort(byDateDesc),
      chauffeur_paidout: bookings.filter((b) => b.booking_type === "chauffeur" && b.status === "completed" && b.driver_paid).sort(byDateDesc),
      tours_pre: bookings.filter((b) => b.booking_type === "tour" && (b.status === "pre_booking" || b.status === "current")).sort(byDateAsc),
      tours_completed: bookings.filter((b) => b.booking_type === "tour" && b.status === "completed" && !b.driver_paid).sort(byDateDesc),
      tours_paidout: bookings.filter((b) => b.booking_type === "tour" && b.status === "completed" && b.driver_paid).sort(byDateDesc),
    };
  }, [bookings]);

  // Double-booking detection for the Submitted tab. A submitted booking is "double-booked"
  // when its vehicle + time window overlaps another active (non-cancelled/completed) booking.
  // Each affected vehicle gets its own conflict number/color so multiple double-bookings on
  // different cars are individually identifiable.
  const doubleBookMarks = useMemo(() => {
    const t = (s) => { const v = Date.parse(s); return isNaN(v) ? null : v; };
    const overlaps = (a, b) => {
      const as = t(a.start_datetime), ae = t(a.end_datetime), bs = t(b.start_datetime), be = t(b.end_datetime);
      if (as == null || ae == null || bs == null || be == null) return false;
      return as < be && ae > bs;
    };
    const active = bookings.filter((b) => b.vehicle_id && b.status !== "cancelled" && b.status !== "completed");
    const marks = {};
    const vehicleGroup = {};
    let n = 0;
    // Mark double-bookings in BOTH the Submitted queue and the Pre Bookings tab so a
    // confirmed double-booking stays clearly flagged after it moves to Pre Bookings.
    for (const b of [...filtered.submitted, ...filtered.pre_booking]) {
      if (!b.vehicle_id || marks[b.id]) continue;
      const conflict = active.find((o) => o.id !== b.id && o.vehicle_id === b.vehicle_id && overlaps(o, b));
      if (conflict) {
        if (!(b.vehicle_id in vehicleGroup)) { n += 1; vehicleGroup[b.vehicle_id] = n; }
        marks[b.id] = { n: vehicleGroup[b.vehicle_id], vehicle: b.vehicle_label || b.vehicle_plate || "vehicle", with: conflict.customer_name || "another booking" };
      }
    }
    return marks;
  }, [bookings, filtered.submitted, filtered.pre_booking]);

  const CONFLICT_COLORS = ["bg-red-500/15 text-red-400 border-red-500/30", "bg-amber-500/15 text-amber-400 border-amber-500/30", "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30", "bg-cyan-500/15 text-cyan-300 border-cyan-500/30", "bg-lime-500/15 text-lime-300 border-lime-500/30", "bg-orange-500/15 text-orange-300 border-orange-500/30"];
  const renderDoubleBookMark = (b) => {
    const m = doubleBookMarks[b.id];
    if (!m) return null;
    const color = CONFLICT_COLORS[(m.n - 1) % CONFLICT_COLORS.length];
    return (
      <div className="mt-2" data-testid={`double-book-mark-${b.id}`}>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${color}`}
              title={`Double-booked with ${m.with}`}>
          <Warning size={11} weight="fill" /> Double-book #{m.n} · {m.vehicle}
        </span>
      </div>
    );
  };

  const renderPaidMark = (b) => b.driver_paid ? (
    <div className="mt-2" data-testid={`driver-paid-mark-${b.id}`}>
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/12 text-emerald-400 text-[11px] font-medium" title={`Driver paid${b.driver_pay_amount ? " $" + b.driver_pay_amount : ""}${b.driver_paid_at ? " on " + new Date(b.driver_paid_at).toLocaleDateString() : ""}`}>
        <CheckCircle size={11} weight="fill" /> Driver paid{b.driver_name ? ` · ${b.driver_name}` : ""}
      </span>
    </div>
  ) : null;

  const openNew = () => navigate("/new-booking");

  const markPayout = async (b, paid) => {
    try {
      await api.post(`/bookings/${b.id}/toggle-payout`, { paid });
      toast.success(paid ? "Marked paid out" : "Moved back to Completed");
      invalidate();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkPay = async (ids, clear) => {
    try {
      const { data } = await api.post(`/bookings/bulk_payout`, { booking_ids: ids, paid: true });
      toast.success(`Marked ${data.updated} job(s) paid out`);
      clear?.();
      invalidate();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const chooseType = (type) => {
    setTypePromptOpen(false);
    setSubPromptOpen(false);
    setFormType(type);
    setEditing(null);
    const fields = type === "rental" ? RENTAL_FIELDS : type === "tour" ? TOUR_FIELDS : CHAUFFEUR_FIELDS;
    const blank = Object.fromEntries([...fields, ...CLIENT_CREDENTIAL_FIELDS].map((f) => [f.name, f.default ?? (f.type === "number" ? 0 : "")]));
    setForm({ ...blank, booking_type: type, status: "pre_booking", booking_date: todayISODate() });
    setFormOpen(true);
  };

  const openEdit = (b) => {
    const type = b.booking_type === "rental" ? "rental" : b.booking_type === "tour" ? "tour" : "chauffeur";
    setFormType(type);
    setEditing(b);
    setForm({ ...b });
    setFormOpen(true);
  };

  const handleVehicleChange = (id) => {
    const v = vehicles.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      vehicle_id: id,
      vehicle_label: v ? `${v.make} ${v.model}${v.year ? " " + v.year : ""}` : "",
      vehicle_plate: v?.license_plate || "",
    }));
  };

  const handleDriverChange = (id) => {
    const d = chauffeurs.find((x) => x.id === id);
    setForm((f) => {
      const pay = driverPayFor({ ...f, booking_type: formType }, d);
      return {
        ...f,
        driver_id: id,
        driver_name: d ? `${d.first_name || ""} ${d.last_name || ""}`.trim() : "",
        driver_cost: Number(f.driver_cost) > 0 ? f.driver_cost : (pay || f.driver_cost),
      };
    });
  };

  const submit = async () => {
    const credErr = validateBooking(form, formType);
    if (credErr) { toast.error(credErr); return; }
    const payload = { ...form, booking_type: formType };
    const fields = formType === "rental" ? RENTAL_FIELDS : formType === "tour" ? TOUR_FIELDS : CHAUFFEUR_FIELDS;
    for (const f of fields) {
      if (f.type === "number" && payload[f.name] !== "" && payload[f.name] !== null) {
        payload[f.name] = Number(payload[f.name]) || 0;
      }
    }
    if (editing) {
      setSaving(true);
      try {
        await api.put(`/bookings/${editing.id}`, payload);
        toast.success("Booking updated");
        setFormOpen(false); invalidate();
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setSaving(false); }
      return;
    }
    // SALES-017: only a NEW booking asks — an edit's lead question was already
    // answered when it was created.
    leadMatch.start(payload);
  };

  const doSave = async (payload, { linked } = {}) => {
    setSaving(true);
    try {
      const { data } = await api.post("/bookings", payload);
      // SALES-018: a lead_id came back that was never sent = the server created
      // the lead behind this booking.
      const leadMade = !payload.lead_id && !!data?.lead_id;
      const fromDate = data?.auto_linked?.came_in
        ? new Date(data.auto_linked.came_in).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Toronto" })
        : "";
      toast.success(
        data?.auto_linked ? `Booking created — linked to the lead from ${fromDate}`
          : linked ? "Booking created and linked to the lead"
            : leadMade ? "Booking created, and a lead recorded for this customer"
              : "Booking created",
      );
      setFormOpen(false); invalidate();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const leadMatch = useLeadMatch(doSave);

  const transition = async (id, status, label) => {
    try {
      await api.post(`/bookings/${id}/transition?new_status=${status}`);
      toast.success(`Moved to ${label}`);
      invalidate();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendBack = async () => {
    if (!rejectTarget) return;
    try {
      await api.post(`/bookings/${rejectTarget.id}/transition`, null, { params: { new_status: "rejected", reason: rejectReason } });
      toast.success("Sent back to agent");
      setRejectTarget(null); setRejectReason(""); invalidate();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    const reason = window.prompt("Delete this booking? It moves to the recycle bin (Settings → Deleted bookings), where an admin can restore it.\n\nOptional reason:");
    if (reason === null) return;
    try { await api.delete(`/bookings/${id}`, { params: { reason } }); toast.success("Moved to recycle bin"); invalidate(); }
    catch (e) { console.error("Delete failed:", e); toast.error(formatApiError(e)); }
  };

  // Navigate into a booking detail while remembering: (a) where to send the
  // user on "Back", and (b) the ordered id-list of the tab they came from so
  // the detail page can offer Prev / Next paging.
  const openBooking = (row, list) => {
    const returnTo = `${location.pathname}?${searchParams.toString()}`;
    const listIds = (list || []).map((r) => r.id);
    navigate(`/bookings/view/${row.id}`, { state: { returnTo, listIds } });
  };

  const tabDefs = [
    ...(section === "submitted" ? [{ key: "submitted", label: "Submitted", count: filtered.submitted.length, testid: "tab-submitted" }] : []),
    ...(section === "pre" ? [{ key: "pre_booking", label: "Pre-bookings", count: filtered.pre_booking.length, testid: "tab-pre-bookings" }] : []),
    ...((!section || section === "rental") ? [
      { key: "rental_pre", label: "Pre-bookings", count: filtered.rental_pre.length, testid: "tab-rental-pre" },
      { key: "rental_current", label: "Current", count: filtered.rental_current.length, testid: "tab-rental-current" },
      { key: "rental_completed", label: "Completed", count: filtered.rental_completed.length, testid: "tab-rental-completed" },
      { key: "salespeople", label: "Salespeople", testid: "tab-salespeople" },
    ] : []),
    ...(section === "chauffeur" ? [
      { key: "chauffeur_pre", label: "Pre Bookings", count: filtered.chauffeur_pre.length, testid: "tab-chauffeur-pre" },
      { key: "chauffeur_completed", label: "Completed", count: filtered.chauffeur_completed.length, testid: "tab-chauffeur-completed" },
      { key: "chauffeur_paidout", label: "Paid out", count: filtered.chauffeur_paidout.length, testid: "tab-chauffeur-paidout" },
      { key: "drivers", label: "Drivers", count: chauffeurs.length, testid: "tab-drivers" },
      { key: "owner_balance", label: "3rd-Party Owners", testid: "tab-owner-balance" },
    ] : []),
    ...(section === "tours" ? [
      { key: "tours_pre", label: "Pre Bookings", count: filtered.tours_pre.length, testid: "tab-tours-pre" },
      { key: "tours_completed", label: "Completed Tours", count: filtered.tours_completed.length, testid: "tab-tours-completed" },
      { key: "tours_paidout", label: "Paid out Tours", count: filtered.tours_paidout.length, testid: "tab-tours-paidout" },
      { key: "tours_packages", label: "Packages", testid: "tab-tours-packages" },
    ] : []),
  ];

  return (
    <div className="space-y-6" data-testid="bookings-page">
      {importResult && (
        <ChauffeurImportSummary
          result={importResult}
          vehicles={vehicles}
          onClose={() => setImportResult(null)}
          onAssigned={invalidate}
        />
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="label-mono">All booking pipelines</div>
        <div className="flex items-center gap-2">
          {section === "chauffeur" && (
            <>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onImportChauffeur} className="hidden" data-testid="chauffeur-import-input" />
              <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-chauffeur-btn">
                <UploadSimple size={16} className="mr-1.5" /> {importing ? "Importing…" : "Import Chauffeur CSV"}
              </Button>
            </>
          )}
          <Button onClick={openNew} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="bookings-create-button">
            <Plus size={16} className="mr-1.5" /> New Booking
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <div className="mb-6" data-testid="bookings-tabs">
          <PillTabs tabs={tabDefs} value={tab} onChange={changeTab} testid="tab" />
        </div>

        <TabsContent value="submitted">
          {(() => {
            const submittedActions = (b) => (
              <>
                {b.source === "website" && (
                  <Button size="sm" variant="outline" className="rounded-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" onClick={() => setContactTarget(b)} title="Log who contacted this website request — it moves to Leads ▸ Website Leads" data-testid={`website-contacted-${b.id}`}><PhoneCall size={12} className="mr-1" />Contacted ✓</Button>
                )}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => transition(b.id, b.booking_type === "rental" ? "pre_booking" : "current", b.booking_type === "rental" ? "Pre Bookings" : "Confirmed")} data-testid={`booking-confirm-${b.id}`}><CheckCircle size={12} className="mr-1" />Confirm</Button>
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { setRejectTarget(b); setRejectReason(""); }} data-testid={`booking-sendback-${b.id}`} title="Send back to agent for edits"><ArrowUUpLeft size={14} className="mr-1 text-amber-500" />Send back</Button>
              </>
            );
            return (
              <div className="space-y-8">
                {filtered.submitted_website.length > 0 && (
                  <div data-testid="submitted-website-section" className="rounded-2xl border border-emerald-500/35 bg-emerald-500/[0.04] p-5">
                    <div className="flex items-center gap-2.5 mb-4">
                      <Globe size={17} className="text-emerald-400" />
                      <h3 className="font-heading text-lg text-emerald-300">Website Bookings</h3>
                      <span className="tnum text-sm text-muted-foreground">{filtered.submitted_website.length}</span>
                      <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">— from the public booking form. Hit “Contacted ✓” to move one into Leads ▸ Website Leads.</span>
                      {isAdmin && (
                        <Button size="sm" variant="outline" className="ml-auto rounded-full" onClick={() => setMoveRows(filtered.submitted_website)} data-testid="website-move-all">
                          Move all to Leads
                        </Button>
                      )}
                    </div>
                    <BookingTable rows={filtered.submitted_website} loading={loading} testid="submitted-website" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.submitted_website)} rowMarker={renderDoubleBookMark} actions={submittedActions} />
                  </div>
                )}
                <div data-testid="submitted-rental-section">
                  <div className="flex items-center gap-2.5 mb-4">
                    <Car size={17} className="text-primary" />
                    <h3 className="font-heading text-lg">Rental</h3>
                    <span className="tnum text-sm text-muted-foreground">{filtered.submitted_rental.length}</span>
                  </div>
                  <BookingTable rows={filtered.submitted_rental} loading={loading} testid="submitted-rental" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.submitted_rental)} rowMarker={renderDoubleBookMark} actions={submittedActions} />
                </div>
                <div data-testid="submitted-chauffeur-section">
                  <div className="flex items-center gap-2.5 mb-4">
                    <IconChauffeur size={17} className="text-primary" />
                    <h3 className="font-heading text-lg">Chauffeur / Tour</h3>
                    <span className="tnum text-sm text-muted-foreground">{filtered.submitted_chauffeur_tour.length}</span>
                  </div>
                  <BookingTable rows={filtered.submitted_chauffeur_tour} loading={loading} testid="submitted-chauffeur" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.submitted_chauffeur_tour)} rowMarker={renderDoubleBookMark} actions={submittedActions} />
                </div>
              </div>
            );
          })()}
        </TabsContent>
        <TabsContent value="pre_booking">
          <BookingTable rows={filtered.pre_booking} loading={loading} testid="pre" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.pre_booking)} rowMarker={renderDoubleBookMark} actions={(b) => (
            b.booking_type === "rental"
              ? <Button size="sm" variant="outline" onClick={() => transition(b.id, "current", "Current")}><ArrowRight size={12} className="mr-1" />Send out</Button>
              : <Button size="sm" variant="outline" onClick={() => transition(b.id, "current", "Confirmed")}><CheckCircle size={12} className="mr-1" />Confirm</Button>
          )} />
        </TabsContent>
        <TabsContent value="rental_pre">
          <BookingTable rows={filtered.rental_pre} loading={loading} testid="rental-pre" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.rental_pre)} rowMarker={renderDoubleBookMark} actions={(b) => (
            <Button size="sm" variant="outline" onClick={() => transition(b.id, "current", "Current")}><ArrowRight size={12} className="mr-1" />Send out</Button>
          )} />
        </TabsContent>
        <TabsContent value="rental_current">
          <BookingTable rows={filtered.rental_current} loading={loading} testid="rental-current" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.rental_current)} actions={(b) => (
            <Button size="sm" variant="outline" onClick={() => transition(b.id, "completed", "Completed")}><CheckCircle size={12} className="mr-1" />Complete</Button>
          )} />
        </TabsContent>
        <TabsContent value="rental_completed">
          <BookingTable rows={filtered.rental_completed} loading={loading} testid="rental-completed" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.rental_completed)} />
        </TabsContent>
        <TabsContent value="salespeople">
          <SalespeoplePanel />
        </TabsContent>
        <TabsContent value="chauffeur_pre">
          <BookingTable rows={filtered.chauffeur_pre} loading={loading} testid="chauffeur-pre" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.chauffeur_pre)} actions={(b) => (
            b.status === "current"
              ? <Button size="sm" variant="outline" onClick={() => transition(b.id, "completed", "Completed")}><CheckCircle size={12} className="mr-1" />Complete</Button>
              : <Button size="sm" variant="outline" onClick={() => transition(b.id, "current", "Confirmed")}><CheckCircle size={12} className="mr-1" />Confirm</Button>
          )} />
        </TabsContent>
        <TabsContent value="chauffeur_completed">
          <BookingTable rows={filtered.chauffeur_completed} loading={loading} testid="chauffeur-completed" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.chauffeur_completed)} rowMarker={renderPaidMark} selectable bulkActions={(ids, clear) => (
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => bulkPay(ids, clear)} data-testid="bulk-pay-selected"><CheckCircle size={12} className="mr-1" />Pay selected ({ids.length})</Button>
          )} actions={(b) => (
            <Button size="sm" variant="outline" onClick={() => markPayout(b, true)} data-testid={`mark-paidout-${b.id}`}><CheckCircle size={12} className="mr-1 text-emerald-500" />Mark paid out</Button>
          )} />
        </TabsContent>
        <TabsContent value="chauffeur_paidout">
          <BookingTable rows={filtered.chauffeur_paidout} loading={loading} testid="chauffeur-paidout" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.chauffeur_paidout)} rowMarker={renderPaidMark} actions={(b) => (
            <Button size="sm" variant="ghost" onClick={() => markPayout(b, false)} data-testid={`undo-paidout-${b.id}`}><ArrowUUpLeft size={14} className="mr-1 text-amber-500" />Undo payout</Button>
          )} />
        </TabsContent>
        <TabsContent value="drivers">
          <DriversPanel chauffeurs={chauffeurs} bookings={bookings} onChange={invalidate} />
        </TabsContent>
        <TabsContent value="owner_balance">
          <OwnerBalancePanel bookings={bookings} vehicles={vehicles} chauffeurs={chauffeurs} onChange={invalidate} />
        </TabsContent>
        <TabsContent value="tours_pre">
          <BookingTable rows={filtered.tours_pre} loading={loading} testid="tours-pre" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.tours_pre)} actions={(b) => (
            b.status === "current"
              ? <Button size="sm" variant="outline" onClick={() => transition(b.id, "completed", "Completed")}><CheckCircle size={12} className="mr-1" />Complete</Button>
              : <Button size="sm" variant="outline" onClick={() => transition(b.id, "current", "Confirmed")}><CheckCircle size={12} className="mr-1" />Confirm</Button>
          )} />
        </TabsContent>
        <TabsContent value="tours_completed">
          <BookingTable rows={filtered.tours_completed} loading={loading} testid="tours-completed" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.tours_completed)} rowMarker={renderPaidMark} selectable bulkActions={(ids, clear) => (
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => bulkPay(ids, clear)} data-testid="bulk-pay-selected"><CheckCircle size={12} className="mr-1" />Pay selected ({ids.length})</Button>
          )} actions={(b) => (
            <Button size="sm" variant="outline" onClick={() => markPayout(b, true)} data-testid={`mark-paidout-${b.id}`}><CheckCircle size={12} className="mr-1 text-emerald-500" />Mark paid out</Button>
          )} />
        </TabsContent>
        <TabsContent value="tours_paidout">
          <BookingTable rows={filtered.tours_paidout} loading={loading} testid="tours-paidout" onEdit={openEdit} onDelete={remove} onQuote={setQuoteBooking} onRowClick={(bk) => openBooking(bk, filtered.tours_paidout)} rowMarker={renderPaidMark} actions={(b) => (
            <Button size="sm" variant="ghost" onClick={() => markPayout(b, false)} data-testid={`undo-paidout-${b.id}`}><ArrowUUpLeft size={14} className="mr-1 text-amber-500" />Undo payout</Button>
          )} />
        </TabsContent>
        <TabsContent value="tours_packages">
          <TourPackages />
        </TabsContent>
      </Tabs>

      {/* Type prompt */}
      <Dialog open={typePromptOpen} onOpenChange={setTypePromptOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="booking-type-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">What kind of booking?</DialogTitle>
            <DialogDescription>Choose the service to load the right form.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <button onClick={() => chooseType("rental")} data-testid="choose-rental" className="border border-border rounded-2xl p-6 text-left hover:border-primary hover:bg-white/[0.04] transition-colors">
              <Car size={28} className="text-primary mb-3" />
              <div className="font-heading text-lg">Rental</div>
              <div className="text-xs text-muted-foreground mt-1">Self-drive vehicle rental</div>
            </button>
            <button onClick={() => { setTypePromptOpen(false); setSubPromptOpen(true); }} data-testid="choose-chauffeur-tour" className="border border-border rounded-2xl p-6 text-left hover:border-primary hover:bg-white/[0.04] transition-colors">
              <IconChauffeur size={28} className="text-primary mb-3" />
              <div className="font-heading text-lg">Chauffeur / Tour</div>
              <div className="text-xs text-muted-foreground mt-1">With driver / experience</div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sub-prompt: Chauffeur vs Tour */}
      <Dialog open={subPromptOpen} onOpenChange={setSubPromptOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="booking-subtype-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Chauffeur or Tour?</DialogTitle>
            <DialogDescription>Both include a driver. Tours add an itinerary &amp; passenger count.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <button onClick={() => chooseType("chauffeur")} data-testid="choose-chauffeur" className="border border-border rounded-2xl p-6 text-left hover:border-primary hover:bg-white/[0.04] transition-colors">
              <IconChauffeur size={28} className="text-primary mb-3" />
              <div className="font-heading text-lg">Chauffeur</div>
              <div className="text-xs text-muted-foreground mt-1">Point-to-point with a driver</div>
            </button>
            <button onClick={() => chooseType("tour")} data-testid="choose-tour" className="border border-border rounded-2xl p-6 text-left hover:border-primary hover:bg-white/[0.04] transition-colors">
              <MapTrifold size={28} className="text-primary mb-3" />
              <div className="font-heading text-lg">Tour</div>
              <div className="text-xs text-muted-foreground mt-1">Multi-stop experience / package</div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="booking-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Edit" : "New"} {formType === "rental" ? "Rental" : formType === "tour" ? "Tour" : "Chauffeur"} Booking
            </DialogTitle>
            <DialogDescription>Fill in the booking details below, then save.</DialogDescription>
          </DialogHeader>
          <BookingForm
            formType={formType}
            form={form}
            setForm={setForm}
            vehicles={vehicles}
            chauffeurs={chauffeurs}
            onVehicleChange={handleVehicleChange}
            onDriverChange={handleDriverChange}
            excludeBookingId={editing?.id}
          />
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="bookings-dialog-save">
              {saving ? "Saving…" : editing ? "Save changes" : "Create booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote/Print */}
      <QuoteDialog booking={quoteBooking} onClose={() => setQuoteBooking(null)} />

      {/* SALES-017: an existing lead already has this phone number. */}
      <LeadMatchDialog {...leadMatch.dialog} />

      {/* Send back (reject) dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="sendback-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Send back to agent</DialogTitle>
            <DialogDescription>This returns the booking to the agent's Rejected Bookings so they can edit and resubmit it.</DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label className="label-mono mb-1.5 block">Reason / what to fix (optional)</Label>
            <Textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Missing deposit amount, wrong dates…" className="bg-input border-border" data-testid="sendback-reason" />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button onClick={sendBack} className="bg-amber-600 text-white hover:bg-amber-600/90" data-testid="sendback-confirm"><ArrowUUpLeft size={14} className="mr-1.5" />Send back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* WEB-001: move every waiting website request to Leads, once */}
      <Dialog open={!!moveRows} onOpenChange={(o) => { if (!o && !moving) setMoveRows(null); }}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="website-move-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Move {moveRows?.length || 0} website {moveRows?.length === 1 ? "request" : "requests"} to Leads?</DialogTitle>
            <DialogDescription>
              Each one becomes a new, unassigned lead on the Leads page with everything the customer filled in, and leaves Submitted Bookings and the fleet calendar. The booking copy goes to the recycle bin as "Converted to lead". Older requests are not texted to B squad; one that came in within the B squad claim window is offered like any new lead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMoveRows(null)} disabled={moving}>Cancel</Button>
            <Button onClick={moveAll} disabled={moving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="website-move-confirm">
              {moving ? "Moving…" : "Move to Leads"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Website request → Website Lead */}
      {contactTarget && (
        <WebsiteContactDialog
          booking={contactTarget}
          team={leadsTeam}
          onClose={() => setContactTarget(null)}
          onDone={invalidate}
        />
      )}
    </div>
  );
}
